import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MapPin, Building2 } from "lucide-react";
import { getStateUrl, getCityUrl } from "../constants";
import { getStates, getStatesSync, getContent, getLocalContent } from "../api";
import SEO from "../components/SEO";
import { buildBreadcrumbSchema } from "../utils/seoSchemas";

const StatesIndex = () => {
  const [params] = useSearchParams();
  const q = (params.get("q") || "").toLowerCase();
  const [states, setStates] = useState(() => getStatesSync());
  const [content, setContent] = useState(() => getLocalContent());

  useEffect(() => {
    getStates().then((data) => { if (data?.length) setStates(data); }).catch(() => {});
    getContent().then((data) => { if (data) setContent(data); }).catch(() => {});
  }, []);

  const pageTitle = content?.statesDirectoryTitle || "Browse All Cities by State";
  const pageSubtitle = content?.statesDirectorySubtitle || "Explore mini golf courses in Alabama and all 50 states. Click a state to view its courses, or jump straight to a city.";

  const statesWithData = useMemo(
    () =>
      states.filter((s) =>
        q ? s.name.toLowerCase().includes(q) || s.cities.some((c) => c.toLowerCase().includes(q)) : true
      ),
    [q, states]
  );

  const statesSchemas = [
    buildBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: pageTitle, url: "/states" },
    ]),
  ];

  return (
    <div className="min-h-[60vh] bg-[#f8fafc]" data-testid="states-index-page">
      <SEO
        title={`${pageTitle} | Mini Golf USA`}
        description={pageSubtitle.slice(0, 160)}
        canonicalUrl="https://minigolfalabama.com/states"
        ogType="website"
        schema={statesSchemas}
      />
      <div className="bg-[#181a20] text-white border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-[#e5243b]" />
            </div>
            <h1 className="text-3xl sm:text-5xl font-black text-white tracking-tight">
              {pageTitle}
            </h1>
          </div>
          <p className="mt-3 text-gray-300 max-w-2xl">
            {pageSubtitle}
          </p>
          {q && (
            <p className="mt-2 text-sm text-[#e5243b] font-medium" data-testid="search-results-label">
              Showing results for "{params.get("q")}"
            </p>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="states-list">
          {statesWithData.map((s) => (
            <div
              key={s.code}
              className={`bg-white border rounded-2xl p-6 transition-all ${
                s.code === "al" ? "border-red-300 shadow-md ring-1 ring-red-200" : "border-gray-200 hover:shadow-md"
              }`}
            >
              <div className="flex items-center justify-between">
                <Link
                  to={getStateUrl(s.code)}
                  className="text-lg font-bold text-gray-900 hover:text-[#e5243b] transition-colors"
                >
                  {s.name} {s.code === "al" && "⭐"}
                </Link>
                <span className={`text-xs font-bold px-3 py-1 rounded-full ${
                  s.code === "al" ? "bg-red-50 text-[#e5243b]" : "bg-gray-100 text-gray-700"
                }`}>
                  {s.count} {s.count === 1 ? "course" : "courses"}
                </span>
              </div>
              {s.cities.length > 0 ? (
                <div className="flex flex-wrap gap-2 mt-3">
                  {s.cities.slice(0, 6).map((city) => (
                    <Link
                      key={city}
                      to={getCityUrl(s.code, city)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-50 border border-gray-200 rounded-full text-xs text-gray-600 hover:border-red-400 hover:text-[#e5243b] transition-colors"
                    >
                      <MapPin className="w-3 h-3" /> {city}
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400 mt-3">No courses listed yet</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StatesIndex;
